import React, { useState } from 'react'

import { Col, Spacer } from '@amzn/stencil-react-components/layout'
import { useBreakpoints } from '@amzn/stencil-react-components/responsive'

import { ItemEditorBasicInfo } from 'src/components/ItemEditorBasicInfo'
import { MediaManager } from 'src/components/MediaContentsEditor/MediaManager'
import { ResponsiveRow } from 'src/components/ResponsiveRow'
import { isDefaultLocale } from 'src/contexts/ModuleLocaleContext'
import { useItemEntity, useValidationErrorEntity } from 'src/hooks/useEntity'
import { useSetLocaleWiseMediaOpt } from 'src/hooks/useSetLocaleWiseMediaOpt'
import { MediaStimulusItemDTO } from 'src/models/dto/items/MediaStimulusItemDTO'
import { ItemEditorProps } from 'src/pages/module-builder/item-editors/ItemEditorContainer'
import {
    ItemEditorCheckboxInput,
    ItemEditorTextInput,
} from 'src/pages/module-builder/item-editors/ItemEditorInputs'
import { ItemEditorValidationErrors } from 'src/pages/module-builder/item-editors/ItemEditorValidationErrors'
import { MediaStimulusHandler } from 'src/services/EntityServices/ItemUpdateHandlers/MediaStimulusHandler'

export const MediaStimulusItemEditor = ({
    itemId,
    locale,
    workflowEntityId,
    showValidationError,
    editDisabled,
}: ItemEditorProps) => {
    const { entity: itemDTO } = useItemEntity<MediaStimulusItemDTO>({
        entityId: itemId,
        workflowEntityId,
    })

    const { entity: validationErrorEntity } = useValidationErrorEntity(itemId)

    const { matches } = useBreakpoints()

    const [playbackCount, setPlaybackCount] = useState('')

    const configurePlaybackCount = (limitPlayback: boolean) => {
        if (limitPlayback) {
            setPlaybackCount('1')
            MediaStimulusHandler.updateMaxPlaybackCount(itemDTO.id, 1)
        } else {
            setPlaybackCount('')
            MediaStimulusHandler.updateMaxPlaybackCount(itemDTO.id, undefined)
        }
    }

    const setLocaleWiseMediaOpt = useSetLocaleWiseMediaOpt(itemDTO)

    return (
        <>
            <ItemEditorValidationErrors {...{ itemId, showValidationError }} />
            <ItemEditorBasicInfo
                handler={MediaStimulusHandler}
                itemId={itemId}
                workflowEntityId={workflowEntityId}
                locale={locale}
                editDisabled={editDisabled}
                showValidationError={showValidationError}
            >
                <Col
                    width={matches.s ? '100%' : 'default'}
                    gridGap={'S200'}
                    padding={{ top: `${matches.s ? '8px' : '35px'}` }}
                >
                    <ItemEditorCheckboxInput
                        inputId={'limit-playback'}
                        dataTestId={'limit-playback'}
                        itemId={itemDTO.id}
                        labelText={'Limit Playback'}
                        disabled={!isDefaultLocale(locale) || editDisabled}
                        value={itemDTO.limitPlayback}
                        setValue={(nextValue: boolean) => {
                            configurePlaybackCount(nextValue)
                            MediaStimulusHandler.updateLimitPlayback(itemDTO.id, nextValue)
                        }}
                    />
                </Col>
                {itemDTO.limitPlayback && (
                    <Col width={matches.s ? '100%' : 'default'}>
                        <ItemEditorTextInput
                            inputId={'playback-quantity'}
                            dataTestId={'playback-quantity'}
                            itemId={itemDTO.id}
                            placeholder={'Number of Playbacks'}
                            labelText={'Playback Quantity'}
                            disabled={!isDefaultLocale(locale) || editDisabled}
                            type={'number'}
                            value={itemDTO.maxPlaybackCount?.toString() || playbackCount}
                            setValue={(nextValue: string) => {
                                MediaStimulusHandler.updateMaxPlaybackCount(
                                    itemDTO.id,
                                    parseInt(nextValue)
                                )
                            }}
                            validationErrorMessage={(
                                validationErrorEntity.validationErrors.maxPlaybackCount ?? []
                            ).join(', ')}
                            showError={showValidationError && itemDTO.limitPlayback}
                        />
                    </Col>
                )}
            </ItemEditorBasicInfo>
            <Spacer height={'S200'} />
            <ResponsiveRow>
                <Col width={'100%'}>
                    <ItemEditorTextInput
                        inputId={'title'}
                        dataTestId={'media-title'}
                        value={itemDTO.titleI18N[locale] ?? ''}
                        placeholder={'Title to describe media stimulus'}
                        itemId={itemDTO.id}
                        disabled={editDisabled}
                        setValue={(nextValue: string) => {
                            MediaStimulusHandler.updateTitle(itemDTO.id, locale, nextValue)
                        }}
                        labelText={'Title'}
                    />
                </Col>
            </ResponsiveRow>
            <MediaManager
                itemId={itemDTO.id}
                locale={locale}
                localeWiseMediaOpt={itemDTO.localeWiseMedia}
                maxNumberOfMedia={1}
                setLocaleWiseMediaOpt={setLocaleWiseMediaOpt}
                validationErrorMessage={(
                    validationErrorEntity.validationErrors.localeWiseMedia ?? []
                ).join(', ')}
                showError={showValidationError}
                disabled={editDisabled}
            />
        </>
    )
}
